import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  IWizardStepState,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
  ZnSimpleSelect,
} from '@zendra/shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { COLUMN_HEADER_KEYS, ERROR_MODAL_OPTIONS, STEP_INFO } from './step-complete-other-parameters.config';
import { IStepDataItem } from '../interfaces/IStepDataItem';
import { StandCycleRotationOrchestratorService } from 'orchestrator';
import { HarvestPreparationCatalogs } from '../services/harvest-preparation-catalogs';
import {
  buildPreviousStepMeta,
  getSelectOptionLabel,
  getPreviousStepData,
  isApplyHerbicideSelected,
  persistStepData,
  shouldReuseCurrentStepData,
} from '../helpers/harvest-preparation.helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, firstValueFrom, map, of } from 'rxjs';
import {
  HarvestPreparationRowValidationDto,
  HarvestPreparationStepValidationResultDto,
  RefreshHarvestPreparationStep3ItemDto,
  HarvestPreparationValidationErrorDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-cycle-rotation/harvest-preparation/models';
import { ModalComponent, ToasterService } from '@abp/ng.theme.shared';
import { IRefreshFailedRecord, IRefreshStep3Response, IValidateStep3RequestItem } from '../interfaces/IStepCompleteOtherParameters';
import { HERBICIDE_PRODUCTIVITY_PARAMETER_GROUP_NAME } from '../services/consts/productivity-parameters-group-names';
import { localizeWithFallback } from '../../../../../shared/src/lib/utils/localization-utils';

const SCANNER_MEASUREMENT_TYPE_NAME = 'Scanner';

type StepCompleteOtherParametersField =
  | 'stumpApplicationId'
  | 'isStumpCandidateToRegrowth'
  | 'applyFeller'
  | 'measurementTypeId';

@Component({
  selector: 'lib-step-complete-other-parameters',
  standalone: true,
  imports: [FormsModule, ZnButton, ZnSimpleSelect, LocalizationPipe, ModalComponent],
  templateUrl: './step-complete-other-parameters.html',
})
export class StepCompleteOtherParameters implements OnInit, WizardStepComponent {
  readonly localizationKeyPrefix = STEP_INFO.localizationKeyPrefix;
  readonly columnHeaderKeys = COLUMN_HEADER_KEYS;
  readonly errorModalOptions = ERROR_MODAL_OPTIONS;

  private readonly service = inject(StandCycleRotationOrchestratorService);
  private readonly catalogService = inject(HarvestPreparationCatalogs);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  loading = signal(false);
  rows = signal<IStepDataItem[]>([]);
  measurementTypeOptions = signal<{ key: number; value: string }[]>([]);
  stepValid = signal(false);
  invalidFields = signal<Record<string, boolean>>({});
  selectedErrorMessages = signal<string[]>([]);
  stumpApplicationOptions = signal<{ key: number; value: string }[]>([]);
  errorTitle = '';
  hasErrors = false;
  readonly hasAnyRowError = computed(() => this.rows().some(row => this.hasRowError(row)));

  private wizardContext: IWizardContext;

  ngOnInit(): void {
    this.loading.set(true);
    this.catalogService
      .loadProductivityParameterOptions(HERBICIDE_PRODUCTIVITY_PARAMETER_GROUP_NAME)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: result => {
          this.stumpApplicationOptions.set(result.items);
        },
        error: error => {
          console.log(error);
        }
      });

    this.catalogService
      .loadMeasurementTypeOptions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: result => {
          this.measurementTypeOptions.set(result.items);
        },
        error: error => {
          console.log(error);
        }
      });
  }

  canLeaveSync(_context: IWizardContext): boolean {
    return !this.loading();
  }

  async canLeave(context: IWizardContext): Promise<boolean> {
    if (this.loading()) {
      return false;
    }

    const mappedDto = this.rows().map(item => this.mapRowToValidationItem(item));

    this.loading.set(true);

    try {
      const result = await firstValueFrom(
        this.service.validateHarvestPreparationStep3({ items: mappedDto }).pipe(
          map(response => {
            return this.applyBackendValidationResult(context, response);
          }),
          catchError(error => {
            console.log(error);
            return of(false);
          }),
          finalize(() => this.loading.set(false)),
        ),
      );

      return result;
    } catch {
      return false;
    }
  }

  onActivate(context: IWizardContext): void {
    this.wizardContext = context;

    const state = context.getStepState<IStepDataItem[]>(
      context.activeStepId(),
    ) as IWizardStepState<IStepDataItem[]>;
    const previousStepData = getPreviousStepData(context);

    if (shouldReuseCurrentStepData(state, previousStepData)) {
      this.rows.set(this.normalizeRowsForStep(state?.data ?? []));
      this.validate(context);
      return;
    }

    this.rows.set(this.normalizeRowsForStep(previousStepData));
    this.validate(context);
  }

  collect(context: IWizardContext): IStepDataItem[] {
    const data = persistStepData(context, {
      data: this.rows(),
      completed: true,
      valid: this.stepValid(),
      meta: buildPreviousStepMeta(context),
    });
    return data;
  }

  onUpdateValues() {
    this.loading.set(true);
    this.service
      .refreshHarvestPreparationStep3Values({
        items: this.rows().map(row => this.mapRowToRefreshItem(row)),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: rawResult => {
          const result = rawResult as IRefreshStep3Response;
          const itemsByStandCycleRotationId = new Map(
            (result.items ?? []).map(item => [item.standCycleRotationId, item]),
          );
          const failedRecordsById = this.getFailedRecordsById(result);

          this.rows.update(rows =>
            rows.map(row => {
              const updatedItem = itemsByStandCycleRotationId.get(row.standCycleRotationId);
              const rowError = failedRecordsById.get(row.standCycleRotationId);
              const shouldClearRowError =
                !rowError && (this.hasRowError(row) || !!row.errorTitle);
              const hasUpdatedValues = !!updatedItem;

              if (!hasUpdatedValues && !rowError && !shouldClearRowError) {
                return row;
              }

              return this.normalizeStep3Row(this.syncOptionNames({
                ...row,
                stumpApplicationId: updatedItem
                  ? (updatedItem.stumpApplicationId ?? row.stumpApplicationId)
                  : row.stumpApplicationId,
                isStumpCandidateToRegrowth: updatedItem
                  ? (updatedItem.isStumpCandidateToRegrowth ?? false)
                  : row.isStumpCandidateToRegrowth,
                applyFeller: updatedItem
                  ? (updatedItem.applyFeller ?? false)
                  : row.applyFeller,
                measurementTypeId: updatedItem?.measurementTypeId ?? row.measurementTypeId,
                isMeasurementTypeModifiedByUser:
                  updatedItem && !this.validProperty(row.measurementTypeId)
                    ? undefined
                    : row.isMeasurementTypeModifiedByUser,
                errorMessages: rowError?.messages,
                errorTitle: rowError?.title,
              }));
            }),
          );
          this.validate(this.wizardContext, { notifyWizard: true });
          
        },
        error: error => {
          console.log(error);
        },
      });
  }

  onFieldChange(
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): void {
    this.syncChangedFieldName(row, field);
    this.markFieldAsModifiedByUser(row, field);
    row.errorMessages = undefined;
    row.errorTitle = undefined;
    this.rows.update(rows => [...rows]);
    this.validate(this.wizardContext, {
      notifyWizard: true,
      changedField: { row, field },
      notifyError: true,
    });
  }

  validProperty(item) {
    const aux = item !== null && item !== undefined && item !== '';
    return aux;
  }

  isFieldInvalid(
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): boolean {
    return !!this.invalidFields()[this.getFieldKey(row, field)];
  }

  hasRowError(row: IStepDataItem): boolean {
    return (row.errorMessages?.length ?? 0) > 0;
  }

  openRowError(row: IStepDataItem): void {
    if (!this.hasRowError(row)) {
      return;
    }

    this.selectedErrorMessages.set(row.errorMessages ?? []);
    this.errorTitle = row.errorTitle || this.getDefaultErrorTitle();
    this.hasErrors = true;
  }

  onErrorModalVisibleChange(visible: boolean): void {
    this.hasErrors = visible;
  }

  getCloseLabel(): string {
    return localizeWithFallback(this.localizationService, 'LanguageService::Close');
  }

  validate(
    context?: IWizardContext,
    options?: {
      notifyWizard?: boolean;
      changedField?: {
        row: IStepDataItem;
        field: StepCompleteOtherParametersField;
      };
      notifyError?: boolean;
    },
  ): boolean {
    const previousInvalidFields = this.invalidFields();
    const nextInvalidFields = this.buildInvalidFields();

    const allValuesValid =
      this.rows().length > 0 &&
      Object.keys(nextInvalidFields).length === 0 &&
      !this.hasBlockingRowErrors();

    this.invalidFields.set(nextInvalidFields);
    this.stepValid.set(allValuesValid);

    this.notifyChangedFieldErrorIfNeeded(
      previousInvalidFields,
      nextInvalidFields,
      options,
    );

    if (!options?.notifyWizard || !context) {
      return allValuesValid;
    }

    persistStepData(context, {
      data: this.rows(),
      valid: allValuesValid,
      meta: buildPreviousStepMeta(context),
    });

    return allValuesValid;
  }

  private buildInvalidFields(): Record<string, boolean> {
    const nextInvalidFields: Record<string, boolean> = {};

    for (const row of this.rows()) {
      this.markInvalidFieldIfNeeded(nextInvalidFields, row, 'stumpApplicationId');
      this.markInvalidFieldIfNeeded(nextInvalidFields, row, 'isStumpCandidateToRegrowth');
      this.markInvalidFieldIfNeeded(nextInvalidFields, row, 'applyFeller');
      this.markInvalidFieldIfNeeded(nextInvalidFields, row, 'measurementTypeId');
    }

    return nextInvalidFields;
  }

  private markInvalidFieldIfNeeded(
    invalidFields: Record<string, boolean>,
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): void {
    if (this.validProperty(row[field])) {
      return;
    }

    invalidFields[this.getFieldKey(row, field)] = true;
  }

  private notifyChangedFieldErrorIfNeeded(
    previousInvalidFields: Record<string, boolean>,
    nextInvalidFields: Record<string, boolean>,
    options?: {
      notifyWizard?: boolean;
      changedField?: {
        row: IStepDataItem;
        field: StepCompleteOtherParametersField;
      };
      notifyError?: boolean;
    },
  ): void {
    if (!options?.changedField || !options.notifyError) {
      return;
    }

    const changedFieldKey = this.getFieldKey(
      options.changedField.row,
      options.changedField.field,
    );
    const becameInvalid =
      !!nextInvalidFields[changedFieldKey] && !previousInvalidFields[changedFieldKey];

    if (!becameInvalid) {
      return;
    }

    this.showInvalidFieldError(
      options.changedField.row,
      options.changedField.field,
    );
  }

  private getFieldKey(
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): string {
    return `${row.standCycleRotationId}:${field}`;
  }

  private showInvalidFieldError(
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): void {
        this.toaster.error(`${ZendraLocalizationServices.Geographic}::StandCycleRotationHarvestPreparation.StepCalculateProductivity.Description`, null, {life: DEFAULT_TOASTER_LIFE });
  }

  private mapRowToValidationItem(item: IStepDataItem): IValidateStep3RequestItem {
    return {
      applyFeller: item.applyFeller,
      dab: item.dab,
      isApplyHerbicideSelected: isApplyHerbicideSelected(
        item.stumpApplicationId,
        item.stumpApplicationName,
        this.stumpApplicationOptions(),
      ),
      isStumpCandidateToRegrowth: item.isStumpCandidateToRegrowth,
      isInventoryValidated: item.isInventoryValidated,
      measurementTypeId: item.measurementTypeId,
      measurementTypeName: item.measurementTypeName,
      productivityParameterId: item.productivityParameterId,
      standCycleRotationId: item.standCycleRotationId,
      standName: item.standName,
      stumpApplicationId: item.stumpApplicationId,
      stumpApplicationName: item.stumpApplicationName,
    };
  }

  private mapRowToRefreshItem(item: IStepDataItem): RefreshHarvestPreparationStep3ItemDto {
    const shouldRestorePreviousMeasurementType =
      !this.validProperty(item.measurementTypeId) &&
      this.validProperty(item.lastNonEmptyMeasurementTypeId) &&
      item.lastNonEmptyMeasurementTypeModifiedByUser === true;

    return {
      standCycleRotationId: item.standCycleRotationId,
      rotationNumber: item.rotationNumber,
      standStatusCode: item.standStatusCode,
      stumpApplicationId: item.stumpApplicationId,
      isStumpCandidateToRegrowth: item.isStumpCandidateToRegrowth,
      applyFeller: item.applyFeller,
      measurementTypeId: shouldRestorePreviousMeasurementType
        ? item.lastNonEmptyMeasurementTypeId
        : item.measurementTypeId,
      isStumpCandidateToRegrowthModifiedByUser:
        item.isStumpCandidateToRegrowthModifiedByUser ?? false,
      isApplyFellerModifiedByUser:
        item.isApplyFellerModifiedByUser ?? false,
      isMeasurementTypeModifiedByUser:
        shouldRestorePreviousMeasurementType
          ? true
          : (item.isMeasurementTypeModifiedByUser ??
            this.inferMeasurementTypeModifiedByUser(item)),
    };
  }

  private normalizeRowsForStep(rows: IStepDataItem[]): IStepDataItem[] {
    return rows.map(row => this.normalizeStep3Row(row));
  }

  private normalizeStep3Row(row: IStepDataItem): IStepDataItem {
    const normalizedRow = {
      ...row,
      isStumpCandidateToRegrowth: row.isStumpCandidateToRegrowth ?? false,
      applyFeller: row.applyFeller ?? false,
      isStumpCandidateToRegrowthModifiedByUser:
        row.isStumpCandidateToRegrowthModifiedByUser ?? false,
      isApplyFellerModifiedByUser:
        row.isApplyFellerModifiedByUser ?? false,
      isMeasurementTypeModifiedByUser:
        row.isMeasurementTypeModifiedByUser ??
        this.inferMeasurementTypeModifiedByUser(row),
    };

    this.syncMeasurementTypeSnapshot(normalizedRow);

    return normalizedRow;
  }

  private markFieldAsModifiedByUser(
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): void {
    if (field === 'isStumpCandidateToRegrowth') {
      row.isStumpCandidateToRegrowthModifiedByUser = true;
    }

    if (field === 'applyFeller') {
      row.isApplyFellerModifiedByUser = true;
    }

    if (field === 'measurementTypeId') {
      row.isMeasurementTypeModifiedByUser = this.validProperty(row.measurementTypeId);

      if (row.isMeasurementTypeModifiedByUser) {
        this.syncMeasurementTypeSnapshot(row);
      }
    }
  }

  private inferMeasurementTypeModifiedByUser(row: IStepDataItem): boolean {
    const measurementTypeName = this.getMeasurementTypeName(row).trim().toUpperCase();

    if (!measurementTypeName) {
      return false;
    }

    return measurementTypeName !== SCANNER_MEASUREMENT_TYPE_NAME.toUpperCase();
  }

  private getMeasurementTypeName(row: IStepDataItem): string {
    if (row.measurementTypeName?.trim()) {
      return row.measurementTypeName;
    }

    return (
      getSelectOptionLabel(this.measurementTypeOptions(), row.measurementTypeId) ?? ''
    );
  }

  private syncChangedFieldName(
    row: IStepDataItem,
    field: StepCompleteOtherParametersField,
  ): void {
    if (field === 'stumpApplicationId') {
      row.stumpApplicationName = getSelectOptionLabel(
        this.stumpApplicationOptions(),
        row.stumpApplicationId,
      );
    }

    if (field === 'measurementTypeId') {
      row.measurementTypeName = getSelectOptionLabel(
        this.measurementTypeOptions(),
        row.measurementTypeId,
      );
    }
  }

  private syncOptionNames(row: IStepDataItem): IStepDataItem {
    return {
      ...row,
      stumpApplicationName: getSelectOptionLabel(
        this.stumpApplicationOptions(),
        row.stumpApplicationId,
      ) ?? row.stumpApplicationName,
      measurementTypeName: getSelectOptionLabel(
        this.measurementTypeOptions(),
        row.measurementTypeId,
      ) ?? row.measurementTypeName,
    };
  }

  private syncMeasurementTypeSnapshot(row: IStepDataItem): void {
    if (!this.validProperty(row.measurementTypeId)) {
      return;
    }

    row.lastNonEmptyMeasurementTypeId = row.measurementTypeId;
    row.lastNonEmptyMeasurementTypeName =
      this.getMeasurementTypeName(row) || row.lastNonEmptyMeasurementTypeName;
    row.lastNonEmptyMeasurementTypeModifiedByUser =
      row.isMeasurementTypeModifiedByUser ??
      this.inferMeasurementTypeModifiedByUser(row);
  }

  private applyBackendValidationResult(
    context: IWizardContext,
    result: HarvestPreparationStepValidationResultDto,
  ): boolean {
    const validationErrorsById = this.getValidationErrorsById(result.rows ?? []);
    const hasInvalidRows = validationErrorsById.size > 0;
    const isValid = (result.isValid ?? !hasInvalidRows) && !hasInvalidRows;

    this.rows.update(rows =>
      rows.map(row => {
        const validationError = validationErrorsById.get(row.standCycleRotationId);
        const shouldClearValidationError =
          !validationError &&
          ((row.errorMessages?.length ?? 0) > 0 || !!row.errorTitle);

        if (!validationError && !shouldClearValidationError) {
          return row;
        }

        return {
          ...row,
          errorMessages: validationError?.messages,
          errorTitle: validationError?.title,
        };
      }),
    );

    this.stepValid.set(isValid);
    persistStepData(context, {
      data: this.rows(),
      valid: isValid,
      meta: buildPreviousStepMeta(context),
    });

    if (!isValid) {
      this.notifyBlockingRowErrorsIfNeeded();
    }

    return isValid;
  }

  private getFailedRecordsById(
    result: IRefreshStep3Response,
  ): Map<number, { messages: string[]; title: string }> {
    const failedRecordsById = new Map<number, { messages: string[]; title: string }>();
    const defaultTitle = result.title?.trim() || this.getDefaultErrorTitle();

    for (const failedRecord of result.failedRecords ?? []) {
      const recordId = failedRecord.standCycleRotationId ?? failedRecord.id;
      const messages = this.normalizeFailedRecordMessages(failedRecord);

      if (!recordId || messages.length === 0) {
        continue;
      }

      failedRecordsById.set(recordId, {
        messages,
        title: defaultTitle,
      });
    }

    return failedRecordsById;
  }

  private getValidationErrorsById(
    invalidRows: HarvestPreparationRowValidationDto[],
  ): Map<number, { messages: string[]; title: string }> {
    const validationErrorsById = new Map<number, { messages: string[]; title: string }>();
    const defaultTitle = this.getDefaultErrorTitle();

    for (const invalidRow of invalidRows) {
      const rowId = invalidRow.standCycleRotationId;
      const messages = this.normalizeValidationMessages(invalidRow.errors ?? []);

      if (!rowId || messages.length === 0) {
        continue;
      }

      validationErrorsById.set(rowId, {
        messages,
        title: defaultTitle,
      });
    }

    return validationErrorsById;
  }

  private normalizeFailedRecordMessages(failedRecord: IRefreshFailedRecord): string[] {
    const messages = [
      ...this.normalizeMessageValue(failedRecord.message),
      ...(failedRecord.messages ?? []),
      ...this.normalizeErrorEntries(failedRecord.errors),
    ]
      .map(message => message?.trim())
      .filter((message): message is string => !!message);

    return [...new Set(messages)];
  }

  private normalizeMessageValue(message?: string | string[] | null): string[] {
    if (Array.isArray(message)) {
      return message.filter((item): item is string => typeof item === 'string');
    }

    return typeof message === 'string' ? [message] : [];
  }

  private normalizeErrorEntries(
    errors?: Array<string | { message?: string | null } | null> | null,
  ): string[] {
    return (errors ?? [])
      .map(error => {
        if (typeof error === 'string') {
          return error;
        }

        return error?.message ?? '';
      })
      .filter((message): message is string => !!message);
  }

  private normalizeValidationMessages(
    errors: HarvestPreparationValidationErrorDto[],
  ): string[] {
    return [...new Set(
      errors
        .map(error => error.message?.trim())
        .filter((message): message is string => !!message),
    )];
  }

  private hasBlockingRowErrors(): boolean {
    return this.rows().some(row => this.hasRowError(row));
  }

  private notifyBlockingRowErrorsIfNeeded(): void {
    const blockingRowErrorsCount = this.getBlockingRowErrorsCount();

    if (blockingRowErrorsCount === 0) {
      return;
    }

    this.toaster.error(`${ZendraLocalizationServices.Geographic}::StandCycleRotationHarvestPreparation.StepCompleteOtherParameters.invalidRowsError`, null, {life: DEFAULT_TOASTER_LIFE, messageLocalizationParams: [blockingRowErrorsCount.toString()] });
  }

  private getBlockingRowErrorsCount(): number {
    return this.rows().filter(row => this.hasRowError(row)).length;
  }

  private getDefaultErrorTitle(): string {
    return this.localizationService.instant('AbpUi::Error');
  }
}
